import { useMedplum } from "@medplum/react";

import { FileInput, Stack, Title } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import * as Sentry from "@sentry/react";

import { DisabledReasonButton, useInvalidSubmitReason } from "@/components/DisabledReason";
import { showSystemErrorNotification, toast } from "@/utils/notifications";

import { submitPracticeCSV } from "./services/onboard-practice";
import {
  OnboardPracticeInitialValues,
  OnboardPracticeSchema,
  OnboardPracticeSchemaType,
} from "./zod-schemas/onboard-practice";

function OnboardPractice() {
  const medplum = useMedplum();
  const [isSubmitting, { open: startSubmitting, close: stopSubmitting }] = useDisclosure();

  const form = useForm<OnboardPracticeSchemaType>({
    initialValues: OnboardPracticeInitialValues,
    validate: zodResolver(OnboardPracticeSchema),
  });
  const invalidSubmitReason = useInvalidSubmitReason(form.isValid());

  const handleSubmit = async (values: OnboardPracticeSchemaType) => {
    try {
      startSubmitting();
      const { csv } = values;
      if (!csv) {
        showSystemErrorNotification("Please upload a CSV file");
        return;
      }

      await submitPracticeCSV(medplum, csv);
      toast({
        title: "Practice onboarded successfully",
        message: "The practice has been onboarded successfully",
        type: "success",
        autoClose: 3000,
      });
      form.reset();
    } catch (error) {
      showSystemErrorNotification();
      Sentry.captureException(error);
    } finally {
      stopSubmitting();
    }
  };

  return (
    <Stack>
      <Title order={2}>Onboard Practice</Title>
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <Stack>
          <FileInput
            clearable
            accept=".csv"
            label="Upload Practice CSV"
            {...form.getInputProps("csv")}
          />
          <DisabledReasonButton
            type="submit"
            loading={isSubmitting}
            disabledReason={invalidSubmitReason}
            data-testid="onboard-practice-upload"
          >
            Upload
          </DisabledReasonButton>
        </Stack>
      </form>
    </Stack>
  );
}

export default OnboardPractice;
